import { css, StyleSheet } from 'aphrodite';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { rolesApi, policiesApi, RoleSummary, Policy, orgsApi } from '@/features/org/api/orgs.api';
import { orgStyles as s } from '@/features/org/components/org.styles';
import SettingsLayout from '@/features/org/components/settings/settings-layout/SettingsLayout';
import { useParams } from '@/shared/hooks/use-router/react-router-hooks';

/**
 * Groups = Roles CRUD.
 *
 * A group bundles one or more permissions (policies). Members are
 * assigned groups on the Members tab; their effective actions are the
 * UNION of every policy across every group they hold.
 *
 * Built-in groups (owner / admin / developer / viewer) are read-only;
 * the editor shows a banner and disables the controls.
 */
export default function GroupsPage() {
  const { slug } = useParams<{ slug: string }>();
  const [orgId, setOrgId] = useState<string | null>(null);
  const [roles, setRoles] = useState<RoleSummary[] | null>(null);
  const [policies, setPolicies] = useState<Policy[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    orgsApi
      .bySlug(slug)
      .then((r) => setOrgId(r.org.id))
      .catch((e) => setErr(e.message));
  }, [slug]);

  const reload = useCallback(async () => {
    if (!orgId) return;
    try {
      const [r, p] = await Promise.all([rolesApi.list(orgId), policiesApi.list(orgId)]);
      setRoles(r.roles);
      setPolicies(p.policies);
      setErr(null);
    } catch (e: any) {
      setErr(e.message);
    }
  }, [orgId]);

  useEffect(() => {
    reload();
  }, [reload]);

  const selected = useMemo(
    () => roles?.find((r) => r.id === selectedId) ?? null,
    [roles, selectedId],
  );

  const handleNew = () => setSelectedId('__new__');
  const handleDelete = async (id: string) => {
    if (!confirm('Delete this role? Members must be reassigned first.')) return;
    try {
      await rolesApi.remove(id);
      await reload();
      setSelectedId(null);
    } catch (e: any) {
      setErr(e.message);
    }
  };

  return (
    <SettingsLayout>
      <div className={css(s.page)} style={{ maxWidth: 'none' }}>
        <div className={css(s.pageHeader)}>
          <div>
            <div className={css(s.pageTitle)}>Roles</div>
            <div className={css(s.pageSubtitle)}>
              A role bundles permissions. Members with a role get every permission attached to it.
            </div>
          </div>
          <button className={css(s.btn)} onClick={handleNew}>
            + New role
          </button>
        </div>

        {err && <div className={css(s.errorBox)}>{err}</div>}

        <div className={css(gp.split)}>
          <div className={css(gp.list)}>
            {!roles ? (
              <div className={css(s.empty)}>Loading…</div>
            ) : (
              roles.map((r) => (
                <button
                  key={r.id}
                  className={css(gp.listItem, selectedId === r.id && gp.listItemActive)}
                  onClick={() => setSelectedId(r.id)}
                >
                  <div className={css(gp.listName)}>{r.name}</div>
                  <div className={css(gp.listDesc)}>
                    {r.policyIds.length} permission{r.policyIds.length === 1 ? '' : 's'}
                    {' · '}
                    {r.memberCount} member{r.memberCount === 1 ? '' : 's'}
                  </div>
                  {r.isBuiltin && <div className={css(gp.builtinChip)}>BUILT-IN</div>}
                </button>
              ))
            )}
          </div>
          <div className={css(gp.detail)}>
            {selectedId === '__new__' && orgId && (
              <GroupEditor
                orgId={orgId}
                policies={policies}
                onSaved={(r) => {
                  reload();
                  setSelectedId(r.id);
                }}
                onCancel={() => setSelectedId(null)}
              />
            )}
            {selected && selectedId !== '__new__' && (
              <GroupEditor
                key={selected.id}
                orgId={orgId!}
                initial={selected}
                policies={policies}
                onSaved={() => reload()}
                onDelete={selected.isBuiltin ? undefined : () => handleDelete(selected.id)}
                onCancel={() => setSelectedId(null)}
              />
            )}
            {!selectedId && (
              <div className={css(s.empty)}>Pick a role on the left to view or edit.</div>
            )}
          </div>
        </div>
      </div>
    </SettingsLayout>
  );
}

function GroupEditor({
  orgId,
  initial,
  policies,
  onSaved,
  onDelete,
  onCancel,
}: {
  orgId: string;
  initial?: RoleSummary;
  policies: Policy[];
  onSaved: (r: RoleSummary) => void;
  onDelete?: () => void;
  onCancel: () => void;
}) {
  const [name, setName] = useState(initial?.name ?? '');
  const [description, setDescription] = useState(initial?.description ?? '');
  const [policyIds, setPolicyIds] = useState<Set<string>>(new Set(initial?.policyIds ?? []));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const readOnly = !!initial?.isBuiltin;

  const togglePolicy = (id: string) => {
    if (readOnly) return;
    setPolicyIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleSave = async () => {
    setBusy(true);
    setErr(null);
    try {
      const payload = {
        name: name.trim(),
        description: description.trim() || undefined,
        policyIds: Array.from(policyIds),
      };
      const res = initial
        ? await rolesApi.update(initial.id, payload)
        : await rolesApi.create({ ...payload, orgId });
      onSaved(res.role);
    } catch (e: any) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      {readOnly && (
        <div className={css(gp.readOnlyBanner)}>
          Built-in role — read-only. Create a new role to customize.
        </div>
      )}
      {err && <div className={css(s.errorBox)}>{err}</div>}
      <div className={css(s.field)}>
        <label className={css(s.label)}>Name</label>
        <input
          className={css(s.input)}
          value={name}
          disabled={readOnly}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Release Manager"
        />
      </div>
      <div className={css(s.field)}>
        <label className={css(s.label)}>Description</label>
        <input
          className={css(s.input)}
          value={description}
          disabled={readOnly}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="What this group is for"
        />
      </div>

      <div className={css(gp.policiesHeader)}>
        Permissions ({policyIds.size}/{policies.length})
      </div>
      <div className={css(gp.policies)}>
        {policies.map((p) => (
          <label key={p.id} className={css(gp.policyRow, readOnly && gp.policyRowDisabled)}>
            <input
              type="checkbox"
              disabled={readOnly}
              checked={policyIds.has(p.id)}
              onChange={() => togglePolicy(p.id)}
            />
            <div className={css(gp.policyText)}>
              <span className={css(gp.policyName)}>
                {p.name}
                {p.isBuiltin && <span className={css(gp.policyBuiltin)}> built-in</span>}
              </span>
              <span className={css(gp.policyDesc)}>
                {p.description || `${p.actions.length} action${p.actions.length === 1 ? '' : 's'}`}
              </span>
            </div>
          </label>
        ))}
      </div>

      {!readOnly && (
        <div
          className={css(s.modalActions)}
          style={{ justifyContent: 'space-between', marginTop: 16 }}
        >
          {onDelete ? (
            <button className={css(s.btnDanger)} onClick={onDelete}>
              Delete role
            </button>
          ) : (
            <span />
          )}
          <div style={{ display: 'flex', gap: 8 }}>
            <button className={css(s.btnGhost)} onClick={onCancel}>
              Cancel
            </button>
            <button className={css(s.btn)} onClick={handleSave} disabled={busy || !name.trim()}>
              {busy ? 'Saving…' : initial ? 'Save changes' : 'Create role'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

const gp = StyleSheet.create({
  split: { display: 'flex', gap: 16, alignItems: 'flex-start' },
  list: {
    width: 280,
    minWidth: 280,
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 4,
    background: 'var(--td-white, #fff)',
    border: '1px solid var(--td-gray-100, #f3f3f3)',
    borderRadius: 8,
    padding: 8,
  },
  listItem: {
    background: 'transparent',
    border: 'none',
    textAlign: 'left' as const,
    padding: '10px 12px',
    borderRadius: 6,
    cursor: 'pointer',
    color: 'var(--td-gray-900, #1a1a1a)',
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 2,
    position: 'relative' as const,
    ':hover': { background: 'var(--td-gray-50, #f9f9f9)' },
  },
  listItemActive: {
    background: 'var(--td-tile-blue-wash, #EEF4FA)',
    color: 'var(--td-tile-blue, #2068A8)',
  },
  listName: { fontSize: 13, fontWeight: 600 },
  listDesc: { fontSize: 11, color: 'var(--td-gray-500, #777)', lineHeight: 1.4 },
  builtinChip: {
    position: 'absolute' as const,
    top: 8,
    right: 8,
    fontSize: 9,
    color: 'var(--td-gray-500, #777)',
    fontWeight: 700,
    letterSpacing: 0.5,
  },
  detail: {
    flex: 1,
    background: 'var(--td-white, #fff)',
    border: '1px solid var(--td-gray-100, #f3f3f3)',
    borderRadius: 8,
    padding: 24,
  },
  readOnlyBanner: {
    padding: 10,
    background: '#FFF8E0',
    border: '1px solid #F0D580',
    borderRadius: 4,
    color: '#7A5500',
    fontSize: 12,
    marginBottom: 12,
  },
  policiesHeader: {
    fontSize: 11,
    color: 'var(--td-gray-500, #777)',
    marginTop: 12,
    marginBottom: 8,
    fontWeight: 600,
    textTransform: 'uppercase' as const,
    letterSpacing: 0.5,
  },
  policies: { display: 'flex', flexDirection: 'column' as const, gap: 8 },
  policyRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '8px 10px',
    border: '1px solid var(--td-gray-100, #f3f3f3)',
    borderRadius: 6,
    cursor: 'pointer',
    ':hover': { borderColor: 'var(--td-gray-400, #a8a8a8)' },
  },
  policyRowDisabled: { opacity: 0.6, cursor: 'default' },
  policyText: { display: 'flex', flexDirection: 'column' as const },
  policyName: { fontSize: 12, fontWeight: 600, color: 'var(--td-gray-900, #1a1a1a)' },
  policyBuiltin: {
    fontSize: 10,
    color: 'var(--td-gray-500, #777)',
    fontWeight: 400,
    marginLeft: 4,
  },
  policyDesc: { fontSize: 11, color: 'var(--td-gray-500, #777)' },
});
